/**
 * Shared layout atoms for the Performance Lab IV Lounge page sections.
 * Built on the global Performance Lab section layout so spacing and the
 * content shell stay consistent with the rest of the collective.
 */
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';
import { PlContentShell, PlSectionRoot } from '../sectionLayout';
import type { SectionWithBgProps } from '../atoms';
import styles from './ivLoungeSectionLayout.module.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/** Light section (matches the default Performance Lab page background). */
export const IvSectionRoot = PlSectionRoot;

/** White section — used for the lead form band on the IV Lounge page. */
export const IvSectionRootWhite = forwardRef<HTMLElement, SectionWithBgProps>(
  function IvSectionRootWhite({ className, ...rest }, ref) {
    return (
      <PlSectionRoot
        ref={ref}
        className={cx(styles.ivSectionRootWhite, className)}
        {...rest}
      />
    );
  },
);

/** Dark section used for the "Why IV therapy" benefits block. */
export const IvSectionRootDark = forwardRef<HTMLElement, SectionWithBgProps>(
  function IvSectionRootDark({ className, ...rest }, ref) {
    return (
      <PlSectionRoot
        ref={ref}
        className={cx(styles.ivSectionRootDark, className)}
        {...rest}
      />
    );
  },
);

/** Left-aligned content shell sized to the IV Lounge design grid. */
export const IvContentShell = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(function IvContentShell({ className, ...rest }, ref) {
  return (
    <PlContentShell
      ref={ref}
      className={cx(styles.ivContentShell, className)}
      {...rest}
    />
  );
});

/** Monospace overline (e.g. "THE LINEUP"). */
export const IvSectionLabel = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'> & { $onDark?: boolean }
>(function IvSectionLabel({ className, $onDark, ...rest }, ref) {
  return (
    <p
      ref={ref}
      data-on-dark={$onDark ? 'true' : undefined}
      className={cx(styles.ivSectionLabel, className)}
      {...rest}
    />
  );
});

/**
 * Dharma display heading (e.g. "CHOOSE YOUR INFUSION"). Matches the standard
 * Performance Lab section heading scale (48px → 72px) used across the collective.
 */
export const IvSectionHeading = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<'h2'> & { $onDark?: boolean }
>(function IvSectionHeading({ className, $onDark, ...rest }, ref) {
  return (
    <h2
      ref={ref}
      data-on-dark={$onDark ? 'true' : undefined}
      className={cx(styles.ivSectionHeading, 'pl-dharma-title', className)}
      {...rest}
    />
  );
});

/**
 * Body copy used across the intro and benefits sections.
 * On dark, muted copy uses the design's de-emphasised grey (#454545).
 */
export const IvBodyText = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'> & { $onDark?: boolean; $muted?: boolean }
>(function IvBodyText({ className, $onDark, $muted, ...rest }, ref) {
  return (
    <p
      ref={ref}
      data-on-dark={$onDark ? 'true' : undefined}
      data-muted={$muted ? 'true' : undefined}
      className={cx(styles.ivBodyText, className)}
      {...rest}
    />
  );
});
